import { type FormEvent, type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { toErrorKey } from '../lib/error-code.js';
import { Button } from '../ui/kit.js';
import { authApi, sessionManager } from './session.js';
import './auth.css';

/**
 * The frame both signed-out screens sit in: one paper card, centred, with the
 * felt patch above the heading.
 *
 * There is no shell here because there is nowhere to go yet. Registration uses
 * the same frame, so it lives beside the screen a parent sees most.
 */
export const AuthCard = ({
  title,
  subtitle,
  onSubmit,
  footer,
  children,
}: {
  title: string;
  subtitle: string;
  onSubmit: (event: FormEvent) => void;
  footer: ReactNode;
  children: ReactNode;
}) => {
  const { t } = useTranslation();

  return (
    <main className="auth">
      <form className="card auth-card" onSubmit={onSubmit}>
        <div className="auth-brand">
          <span className="patch" aria-hidden="true">
            K
          </span>
          {t('app.name')}
        </div>
        <div className="auth-head">
          <h1 className="t-h1">{title}</h1>
          <p className="page-sub">{subtitle}</p>
        </div>
        {children}
        <p className="auth-foot">{footer}</p>
      </form>
    </main>
  );
};

export const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const id = useId();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setErrorKey(null);
    setIsSubmitting(true);

    try {
      const { session } = await authApi.login({ email, password });
      sessionManager.adopt(session);
      void navigate('/', { replace: true });
    } catch (error) {
      // The backend answers wrong password and unknown address with the same
      // code on purpose, so this screen must not try to be more specific.
      setErrorKey(toErrorKey(error));
      setIsSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={t('auth.signInTitle')}
      subtitle={t('auth.signInSubtitle')}
      onSubmit={(event) => void submit(event)}
      footer={
        <>
          {t('auth.noAccount')} <Link to="/register">{t('auth.register')}</Link>
        </>
      }
    >
      <div className="field">
        <label htmlFor={`${id}-email`}>{t('auth.email')}</label>
        <input
          id={`${id}-email`}
          className="input"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`${id}-password`}>{t('auth.password')}</label>
        <input
          id={`${id}-password`}
          className="input"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>

      {errorKey === null ? null : (
        <p className="help err" role="alert">
          {t(errorKey)}
        </p>
      )}

      <Button type="submit" variant="primary" block disabled={isSubmitting}>
        {t(isSubmitting ? 'auth.signingIn' : 'auth.signIn')}
      </Button>
    </AuthCard>
  );
};
